import { action, computed, observable } from 'mobx';
import { IJsonSchema } from '@estee/elc-form';
import { IDropdownOption, IFieldDependencies, IState } from '../../domain/entities/Config/Config';
import ShippingRepository from '../../repositories/ShippingRepository';
import AddressFormStore from '../../domain/entities/AddressFormStore';
import AddressViewController, { IAddressViewControllerConfig } from '../AddressViewController';
import { ICountry } from '../../api/IApiResponses';
import { ADDRESS_TYPE_ID, FIELD_TYPE } from '../../constants/Constants';

interface IPackstationViewControllerConfig extends IAddressViewControllerConfig {
  addressRepository: ShippingRepository;
}

interface IPackstationViewControllerData {
  fieldDependencies: IFieldDependencies;
  formStore: AddressFormStore;
  validationSchema: IJsonSchema;
  statesList: IState[];
  streets: IDropdownOption[];
  cities: IDropdownOption[];
  packstationCountries: ICountry[];
  supportsTypeaheadInput: boolean;
  isLoaded: boolean;
}

export default class PackstationViewController extends AddressViewController {
  @observable packstationCountries: ICountry[];

  @observable isLoaded = false;

  constructor(config: IPackstationViewControllerConfig) {
    super(config);
    this.addressRepository = config.addressRepository;
  }

  @action
  async loaded(): Promise<void> {
    await super.loaded();
    // TODO we should have a new task to use the new drupal schemas for packstation address as well
    if (!this.shouldEnableNewSchema) {
      this.formStore.setAddressSchema({
        properties: this.config.packstationAddressInputSchema,
        required: [],
      }, {
        hasStates: !!this.statesList?.length,
      });
    }
    this.setDefaultValues();
    this.packstationCountries = this.config.packstationCountries;
    const shippingAddress = this.transactionRepository.transaction?.shippingAddress;

    if (shippingAddress?.attributes[FIELD_TYPE.ADDRESS_TYPE] === ADDRESS_TYPE_ID.PACKSTATION
      && !shippingAddress?.isNewAddress
    ) {
      this.formStore.clearStore();
      this.formStore.setAddress(shippingAddress);
    }

    if (shippingAddress?.isNewAddress) {
      const country = this.config.packstationCountries.find(countryItem => countryItem.default) ||
        this.config.packstationCountries.find(countryItem => countryItem.value === this.config.countryId);
      if (country) {
        this.formStore.setFieldValue(FIELD_TYPE.COUNTRY, country.label || undefined);
        this.formStore.setFieldValue(FIELD_TYPE.COUNTRY_ID, country.value);
      }
    }

    await this.loadAddressOptions({ countryId: this.selectedCountryId });

    this.adjustFormValuesBasedOnSchema();

    this.isLoaded = true;
  }

  @computed
  get data(): IPackstationViewControllerData {
    return {
      cities: this.cities,
      fieldDependencies: this.fieldDependencies,
      formStore: this.addressRepository.formStore,
      isLoaded: this.isLoaded,
      packstationCountries: this.packstationCountries,
      statesList: this.statesList,
      streets: this.streets,
      supportsTypeaheadInput: this.config.supportsTypeaheadInput,
      validationSchema: this.validationSchema,
    };
  }
}
